import { FormDefinitionType, ILookupItem, Patient, PatientStatusEventType, RandomisationForm, StatusEventForm } from "@/api/dtos";
import { getPatientStatusSummary } from "@/utils/patientStatusUtils";
import { createKeysMap } from "@/utils/typeUtils";
import { Box, Chip, FormControl, MenuItem, Select, SelectChangeEvent, styled, Tooltip, TooltipProps } from "@mui/material";
import { GridColDef, GridFilterOperator, useGridApiContext } from "@mui/x-data-grid";
import { FormsMetadataContext, IInstitution, useFormLookups, useLookups } from "@ngt/opms";
import { FC, useContext, useMemo, useState } from "react";

const StyledChip = styled(Chip)(({theme}) => ({
    backgroundColor: '#987ba7',
    color: theme.palette.common.white,
}));

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ tooltip: className }} />
))(({ theme }) => ({
    
    backgroundColor: theme.palette.mode === 'dark' ?
        `rgba(0, 0, 0, 0.87)` :
        theme.palette.common.white,
    color: theme.palette.mode === 'dark' ?
        theme.palette.common.white :
        `rgba(0, 0, 0, 0.87)`,
    boxShadow: theme.shadows[1],
    fontSize: 11,
    
}));

const StatusEventFormProperties = createKeysMap<StatusEventForm>();
const RandomisationFormProperties = createKeysMap<RandomisationForm>();

const containsStatusEventOperator: GridFilterOperator<Patient, string> = {
    value: 'containsStatusEvent',
    getApplyFilterFn: filterItem => {
        if (filterItem.value == null || filterItem.value === '') {
            return null;
        }

        const selectedStatus = Number(filterItem.value) as PatientStatusEventType;
        return (_value, patient) => patient.statusEvents?.some(
            ({ statusEvent }) => statusEvent === selectedStatus
        ) ?? false;
    },
};

interface IStatusFilterHeaderProps {
    options: ILookupItem[];
}

const StatusFilterHeader: FC<IStatusFilterHeaderProps> = ({ options }) => {
    const apiRef = useGridApiContext();
    const [status, setStatus] = useState('');

    const onStatusChange = (event: SelectChangeEvent) => {
        const value = event.target.value;
        setStatus(value);
        apiRef.current.setFilterModel({
            items: value
                ? [{ field: 'overallStatus', operator: containsStatusEventOperator.value, value }]
                : [],
        });
    };

    return (
        <FormControl
            variant="standard"
            size="small"
            sx={{ width: 'fit-content' }}
            onClick={event => event.stopPropagation()}
            onMouseDown={event => event.stopPropagation()}
        >
            <Select
                value={status}
                displayEmpty
                disableUnderline
                onChange={onStatusChange}
                renderValue={value => options.find(option => option.id?.toString() === value)?.value ?? 'Status'}
                inputProps={{ 'aria-label': 'Filter by status' }}
                MenuProps={{
                    PaperProps: {
                        sx: theme => ({
                            '& .MuiMenuItem-root': {
                                ...theme.typography.body2,
                            },
                        }),
                    },
                }}
                sx={{
                    color: 'inherit',
                    font: 'inherit',
                    width: 'fit-content',
                    '& .MuiSelect-select': {
                        py: 0,
                        pr: '26px !important',
                        font: 'inherit',
                    },
                }}
            >
                <MenuItem value="">All statuses</MenuItem>
                {options.map(option => (
                    <MenuItem key={option.id} value={option.id?.toString()}>{option.value}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};


const useRandomisedTableColumns = (
    institutions: IInstitution[] | null, 
) => {
    const { data: formsMetadata } = useContext(FormsMetadataContext);
    const {data: statusEventFormlookups} = useLookups(formsMetadata?.find(x => x.formDefinitionId == FormDefinitionType.StatusEventForm));
    const statusEventFormLookupValues = useFormLookups(statusEventFormlookups);
    const {data: randomisationFormLookups} = useLookups(formsMetadata?.find(x => x.formDefinitionId == FormDefinitionType.RandomisationForm));
    const randomisationFormLookupValues = useFormLookups(randomisationFormLookups);


    const statusEventTypeLookupValues = useMemo(() => (
        statusEventFormLookupValues?.find(x => x.propertyName == StatusEventFormProperties.statusEvent!)?.items
    ), [statusEventFormLookupValues]);

    const randomisationArmLookupValues = useMemo(() => (
        randomisationFormLookupValues?.find(x => x.propertyName == RandomisationFormProperties.randomisationArm!)?.items
    ), [randomisationFormLookupValues]);
    
    const columns = useMemo<GridColDef<Patient>[]>(() => [
        {
            field: 'studyNumber',
            headerName: 'Randomisation ID',
            minWidth: 150
        },
        // {
        //     field: 'randomisationArm',
        //     headerName: 'Randomisation Arm',
        //     minWidth: 170,
        //     renderCell: rowData => (
        //         <>{randomisationArmLookupValues?.find(lu => lu.id == rowData.row.treatmentId)?.value}</>
        //     )
        // },
        {
            field: "institutionCode",
            headerName: 'Site Code',
            renderCell: rowData => (
                <StyledChip
                    label={rowData.row.institutionCode}
                    size="small"
                />
            ),
        },
        {
            field: 'institutionId',
            headerName: 'Site Name',
            valueGetter: (_value, row) =>  institutions?.find(i => i.id === row.institutionId)?.name,
            flex: 1,
            //width: 200
        },
        {
            field: 'overallStatus',
            headerName: 'Status',
            filterOperators: [containsStatusEventOperator],
            renderHeader: () => <StatusFilterHeader options={statusEventTypeLookupValues ?? []} />,
            valueGetter: (_value, row) => getPatientStatusSummary(row.statusEvents)?.caption,
            renderCell: ({row}) => {
                const {
                    caption,
                    isPartialWithdrawal,
                    partialWithdrawalStatuses,
                } = getPatientStatusSummary(row.statusEvents);

                if (!isPartialWithdrawal) {
                    return caption;
                }
                return (
                    <Box
                        display={"flex"}
                        justifyContent={"space-between"}

                    >
                        <StyledTooltip
                            title={
                                <Box>
                                    {
                                        partialWithdrawalStatuses.map((x, index) => (
                                            <Box key={`status-event-${row.id}-${x}-${index}`}>
                                                {statusEventTypeLookupValues?.find(lu => lu.id == x)?.value}
                                            </Box>
                                        ))
                                    }
                                </Box>
                            }
                        >
                            <Box>{caption}</Box>
                        </StyledTooltip>
                    </Box>
                )
            },
            width: 220
        }
    ], [institutions, statusEventTypeLookupValues, randomisationArmLookupValues]);

    return columns;
}

export default useRandomisedTableColumns;
